<template>
  <div
    class="upload-list-item"
    :class="{
      'upload-list-item--loading': status === 'loading',
      'upload-list-item--success': status === 'success',
      'upload-list-item--error': status === 'error'
    }"
  >
    <!-- Icon Section -->
    <div class="upload-list-item__icon inline-flex">
      <!-- Loading State -->
      <div v-if="status === 'loading'" class="loading-icon">
        <aha-spinner variant="dark" />
      </div>

      <!-- Success State -->
      <span v-else-if="status === 'success'" class="success-icon ">
        <aha-icon
          maintainColor
          name="file-check"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          class="text-green-500"
        />
      </span>

      <!-- Error State -->
      <span v-else-if="status === 'error'" class="error-icon">
        <aha-icon
          maintainColor
          name="warn-circle-o"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        />
      </span>
    </div>

    <!-- File Name Section -->
    <div class="upload-list-item__filename">
      <span
        class="filename-text"
        :class="{
          'filename-text--error': status === 'error'
        }"
      >
        {{ filename }}
      </span>
      <!-- Progress Bar (only shown during loading) -->
      <div v-if="status === 'loading'" class="progress-section">
        <FakeProgressBar @complete="handleProgressComplete" />
      </div>
    </div>

    <!-- Actions Section -->
    <div class="upload-list-item__actions">
      <aha-icon-button
        v-if="status !== 'loading'"
        :aria-label="`Remove ${filename}`"
        @click="handleRemove"
        @keydown.enter="handleRemove"
        @keydown.space="handleRemove"
        tabindex="0"
        class="delete-button"
        :class="{
          'delete-button--error': status === 'error'
        }"
      >
        <aha-icon
          name="trash"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          :class="{
            'text-gray-500': status === 'success',
            'text-red-500': status === 'error'
          }"
        />
      </aha-icon-button>
    </div>

    <!-- Error Tooltip (only shown on hover for error state) -->
    <div
      v-if="status === 'error' && errorMessage"
      class="error-tooltip"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import FakeProgressBar from './FakeProgressBar.vue'

export default {
  name: 'UploadListItem',
  components: {
    FakeProgressBar,
  },
  props: {
    filename: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
      validator: (value) => ['loading', 'success', 'error'].includes(value),
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  methods: {
    handleRemove() {
      this.$emit('remove')
    },
    handleProgressComplete() {
      // Emit an event when the fake progress is complete
      // You can handle this in the parent component to change status to 'success'
      this.$emit('progress-complete')
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-list-item {
  display: flex;
  align-items: center;
  padding: 24px 32px;
  border-radius: 8px;
  border: 1px #D4D4D4 solid;
  background: white;
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: 8px;
  &:hover {
    @apply bg-purple-10;
  }

  &--success {
    .filename-text {
      color: #374151;
    }
  }

  &--error {
    .filename-text--error {
      color: #EC4899 !important;
    }
    .error-icon {
        color: #FF4081;
    }
    .delete-button--error {
        .text-red-500 {
          color: #EC4899 !important; /* Color/pink/60 */
        }
      }
    &:hover {
      .filename-text--error {
        color: #EC4899; /* Color/pink/60 */
      }
    }
  }

  &__icon {
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
  }

  &__filename {
    flex: 1;
    min-width: 0;
  }

  .progress-section {
    margin-top: 8px;
  }

  .filename-text {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
    display: block;
  }

  &__actions {
    margin-left: 12px;
  }

  .delete-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
  }
}

.error-tooltip {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #374151;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid #374151;
  }
}

.upload-list-item--error:hover .error-tooltip {
  opacity: 1;
  visibility: visible;
}

@keyframes progress-animation {
  0% {
    width: 0%;
  }
  50% {
    width: 60%;
  }
  100% {
    width: 100%;
  }
}

// Success icon styles
.success-icon {
  .text-green-500 {
    color: #16C49A;
  }
}

// Error icon styles

</style>
